// ============================================================
// ELO ENGINE — CONSTANTS & RUNTIME SETTINGS
//
// The default values below are DEFAULTS. The actual values live in
// EngineSettings (which are read from Storage). The ELO functions use
// the EngineSettings fields directly so that changes to settings
// take effect immediately.
//
// EngineSettings is a MUTABLE runtime singleton: there MUST be one shared
// instance across the codebase and elo.ts. Do not
// duplicate the object — both import the same one.
// ============================================================

export const ELO_K_FACTOR_DEFAULT = 32;
export const NEWBIE_MATCH_THRESHOLD_DEFAULT = 20;
export const NEWBIE_GRACE_MATCHES_DEFAULT = 5; // Number of matches with FULL boost before the decay starts
export const NEWBIE_INACTIVE_DAYS_DEFAULT = 180;
export const NEWBIE_K_MULTIPLIER_DEFAULT = 2.0;
export const MOV_MAX_BONUS_DEFAULT = 0.5; // MOV up to 1.0 + 0.5 = 1.5x

/**
 * A COMPLETE set of engine settings — every field present.
 *
 * Distinct from EngineSettingsSaved (all optional, the shape that comes back
 * from storage and may be partial). This is what the engine computes AGAINST:
 * an immutable snapshot taken once per operation, so a result cannot be
 * calculated partly with one configuration and partly with another (#243).
 */
export interface EngineSettingsValues {
  K_FACTOR: number;
  NEWBIE_MATCH_THRESHOLD: number;
  NEWBIE_GRACE_MATCHES: number;
  NEWBIE_INACTIVE_DAYS: number;
  NEWBIE_K_MULTIPLIER: number;
  MOV_MAX_BONUS: number;
}

/** Shape of the saved settings object (all fields optional). */
export interface EngineSettingsSaved {
  K_FACTOR?: number;
  NEWBIE_MATCH_THRESHOLD?: number;
  NEWBIE_GRACE_MATCHES?: number;
  NEWBIE_INACTIVE_DAYS?: number;
  NEWBIE_K_MULTIPLIER?: number;
  MOV_MAX_BONUS?: number;
}

// Engine settings are held in a mutable object so that the UI can update
// the values in Storage and the engine sees the changes immediately without
// having to recreate the engine object.
export const EngineSettings = {
  K_FACTOR: ELO_K_FACTOR_DEFAULT,
  NEWBIE_MATCH_THRESHOLD: NEWBIE_MATCH_THRESHOLD_DEFAULT,
  NEWBIE_GRACE_MATCHES: NEWBIE_GRACE_MATCHES_DEFAULT,
  NEWBIE_INACTIVE_DAYS: NEWBIE_INACTIVE_DAYS_DEFAULT,
  NEWBIE_K_MULTIPLIER: NEWBIE_K_MULTIPLIER_DEFAULT,
  MOV_MAX_BONUS: MOV_MAX_BONUS_DEFAULT,

  // Update from a Storage-loaded object. Missing fields keep their default.
  apply(saved: EngineSettingsSaved | null | undefined) {
    if (!saved || typeof saved !== 'object') return;
    if (typeof saved.K_FACTOR === 'number') this.K_FACTOR = saved.K_FACTOR;
    if (typeof saved.NEWBIE_MATCH_THRESHOLD === 'number')
      this.NEWBIE_MATCH_THRESHOLD = saved.NEWBIE_MATCH_THRESHOLD;
    if (typeof saved.NEWBIE_GRACE_MATCHES === 'number')
      this.NEWBIE_GRACE_MATCHES = saved.NEWBIE_GRACE_MATCHES;
    if (typeof saved.NEWBIE_INACTIVE_DAYS === 'number')
      this.NEWBIE_INACTIVE_DAYS = saved.NEWBIE_INACTIVE_DAYS;
    if (typeof saved.NEWBIE_K_MULTIPLIER === 'number')
      this.NEWBIE_K_MULTIPLIER = saved.NEWBIE_K_MULTIPLIER;
    if (typeof saved.MOV_MAX_BONUS === 'number') this.MOV_MAX_BONUS = saved.MOV_MAX_BONUS;
  },

  // Reset all values to defaults
  reset() {
    this.K_FACTOR = ELO_K_FACTOR_DEFAULT;
    this.NEWBIE_MATCH_THRESHOLD = NEWBIE_MATCH_THRESHOLD_DEFAULT;
    this.NEWBIE_GRACE_MATCHES = NEWBIE_GRACE_MATCHES_DEFAULT;
    this.NEWBIE_INACTIVE_DAYS = NEWBIE_INACTIVE_DAYS_DEFAULT;
    this.NEWBIE_K_MULTIPLIER = NEWBIE_K_MULTIPLIER_DEFAULT;
    this.MOV_MAX_BONUS = MOV_MAX_BONUS_DEFAULT;
  },

  // Export the current values (for saving to Storage)
  serialize() {
    return {
      K_FACTOR: this.K_FACTOR,
      NEWBIE_MATCH_THRESHOLD: this.NEWBIE_MATCH_THRESHOLD,
      NEWBIE_GRACE_MATCHES: this.NEWBIE_GRACE_MATCHES,
      NEWBIE_INACTIVE_DAYS: this.NEWBIE_INACTIVE_DAYS,
      NEWBIE_K_MULTIPLIER: this.NEWBIE_K_MULTIPLIER,
      MOV_MAX_BONUS: this.MOV_MAX_BONUS,
    };
  },

  // Defaults — exposed to allow resetting individual values
  defaults: {
    K_FACTOR: ELO_K_FACTOR_DEFAULT,
    NEWBIE_MATCH_THRESHOLD: NEWBIE_MATCH_THRESHOLD_DEFAULT,
    NEWBIE_GRACE_MATCHES: NEWBIE_GRACE_MATCHES_DEFAULT,
    NEWBIE_INACTIVE_DAYS: NEWBIE_INACTIVE_DAYS_DEFAULT,
    NEWBIE_K_MULTIPLIER: NEWBIE_K_MULTIPLIER_DEFAULT,
    MOV_MAX_BONUS: MOV_MAX_BONUS_DEFAULT,
  },
};

// ============================================================
// TOURNAMENT FORMAT REGISTRIES — shared metadata for all formats
//
// TOURNAMENT TYPE — metadata for the various formats
//
// Returns { id, label, color, abbrev } given a config or a type-id.
// Falls back to 'padel24' for legacy tournaments without a tournamentType field.
// ============================================================
export const TOURNAMENT_TYPES: Record<string, any> = {
  // Padel24 tournaments
  padel24: { id: 'padel24', label: 'Padel24', abbrev: 'P24', color: '#c2f02f', category: 'main' },
  // Club championship (annual)
  // The badge colour carries a white «KM», so it is the AA shade of its pink
  // (#1295, owner decision 2026-11-02): #ec4899 was 3.53:1, #c01074 — the KM
  // category pink from #1293 — is 5.89:1. A DISPLAY constant: no rule reads it.
  klubbmesterskap: {
    id: 'klubbmesterskap',
    label: 'Klubbmesterskap',
    abbrev: 'KM',
    color: '#c01074',
    category: 'championship',
  },
  // League play (season-based series)
  seriespill: {
    id: 'seriespill',
    label: 'Seriespill',
    abbrev: 'SERIE',
    color: '#14b8a6',
    category: 'league',
  },
  // Mini-tournaments
  americano: {
    id: 'americano',
    label: 'Americano',
    abbrev: 'AM',
    color: '#fbbf24',
    category: 'mini',
  },
  mix_americano: {
    id: 'mix_americano',
    label: 'Mix Americano',
    abbrev: 'MAM',
    color: '#fb923c',
    category: 'mini',
  },
  mexicano: { id: 'mexicano', label: 'Mexicano', abbrev: 'MX', color: '#f97316', category: 'mini' },
  mixicano: {
    id: 'mixicano',
    label: 'Mix Mexicano',
    abbrev: 'MMX',
    color: '#ea580c',
    category: 'mini',
  },
  team_americano: {
    id: 'team_americano',
    label: 'Team Americano',
    abbrev: 'TAM',
    color: '#a3e635',
    category: 'mini',
  },
  team_mexicano: {
    id: 'team_mexicano',
    label: 'Team Mexicano',
    abbrev: 'TMX',
    color: '#84cc16',
    category: 'mini',
  },
  club_americano: {
    id: 'club_americano',
    label: 'Club Americano',
    abbrev: 'CAM',
    color: '#22c55e',
    category: 'mini',
  },
  club_team_americano: {
    id: 'club_team_americano',
    label: 'Club Team Americano',
    abbrev: 'CTA',
    color: '#16a34a',
    category: 'mini',
  },
  club_mexicano: {
    id: 'club_mexicano',
    label: 'Club Mexicano',
    abbrev: 'CMX',
    color: '#16a34a',
    category: 'mini',
  },
  // Single matches (not a tournament, but an activity group)
  single_matches: {
    id: 'single_matches',
    label: 'Enkeltkamper',
    abbrev: 'EK',
    color: '#60a5fa',
    category: 'single',
  },
  // A single enkeltmatch run as an ACTIVITY (signup/payment/deadline) — created
  // from a court booking. Exactly 4 players, one best-of-3, ELO via singleMatch.
  // Deliberately NOT in AMERICANO_FORMATS (no rotating-americano result logic).
  single_match: {
    id: 'single_match',
    label: 'Enkeltkamp',
    abbrev: 'EM',
    color: '#0ea5e9',
    category: 'mini',
  },
  // Custom activities (not tournaments) — created directly in Activities
  coaching: {
    id: 'coaching',
    label: 'Coaching',
    abbrev: 'COA',
    color: '#06b6d4',
    category: 'custom',
  },
  arrangement: {
    id: 'arrangement',
    label: 'Arrangement',
    abbrev: 'ARR',
    color: '#8b5cf6',
    category: 'custom',
  },
  custom: {
    id: 'custom',
    label: 'Egendefinert',
    abbrev: 'EGD',
    color: '#64748b',
    category: 'custom',
  },
};

export function getTournamentType(configOrId: any): any {
  if (!configOrId) return TOURNAMENT_TYPES.padel24;
  // League-play registrations use the id 'seriespill_season' — map it to the seriespill type.
  const norm = (id: string) => (id === 'seriespill_season' ? 'seriespill' : id);
  if (typeof configOrId === 'string')
    return TOURNAMENT_TYPES[norm(configOrId)] || TOURNAMENT_TYPES.padel24;
  // Checks both config.tournamentType (Padel24/Americano pattern) and .type (KM pattern)
  const id = norm(configOrId.tournamentType || configOrId.type || 'padel24');
  return TOURNAMENT_TYPES[id] || TOURNAMENT_TYPES.padel24;
}

// Color code for a booking based on ORIGIN (not payment).
// Recurring (seriesId) wins over everything else. Returns { bg, label }.
//   recurring → green, tournament → orange, admin → purple, user → blue
export const BOOKING_SOURCE_STYLES = {
  recurring: { bg: '#16a34a', label: 'Gjentakende' },
  tournament: { bg: '#ea580c', label: 'Turnering' },
  admin: { bg: '#9333ea', label: 'Senter / admin' },
  user: { bg: '#2563eb', label: 'Bruker' },
};
export function bookingSourceKey(b) {
  if (b?.seriesId) return 'recurring';
  if (b?.source === 'tournament') return 'tournament';
  if (b?.source === 'admin') return 'admin';
  return 'user';
}
export function bookingSourceStyle(b) {
  return BOOKING_SOURCE_STYLES[bookingSourceKey(b)];
}

// Flat list of mini-tournament formats (the Americano family) — shared result logic and pad.
export const AMERICANO_FORMATS = [
  'americano',
  'mexicano',
  'mixicano',
  'team_mexicano',
  'mix_americano',
  'club_americano',
  'club_team_americano',
  'club_mexicano',
];
export const CLUB_FORMATS = ['club_americano', 'club_team_americano', 'club_mexicano'];

// ============================================================
// TOURNAMENT RANKING / KM OPTION TABLES
//
// Pure data tables for the ranking-criteria editors + KM scheduling/category
// options, lifted verbatim out of legacy/App.jsx (PR-AV). Threaded into the
// setup/KM views as props (RANKING_OPTIONS_*, KM_COURT_PRIORITY_OPTIONS, …).
// ============================================================
export const RANKING_OPTIONS_PADEL24 = [
  { id: 'wins', label: 'Antall seire', shortLabel: 'Seire', description: 'Flest seire først' },
  {
    id: 'gamesDiff',
    label: 'Games-differanse',
    shortLabel: 'Games-diff',
    description: 'Vunne games minus tapte games',
  },
  {
    // SAYS WHICH MATCHES IT LOOKS AT, because it is not the same window the
    // other criteria use. Wins and games are reset every phase
    // (`resetStandingsForPhase`); this scan is not, so a meeting in an earlier
    // phase still decides. That is current behaviour, and it belongs in the
    // setting rather than being discovered in a group table.
    id: 'h2h',
    label: 'Innbyrdes oppgjør',
    shortLabel: 'Innbyrdes',
    description: 'Hvem vant det direkte oppgjøret — teller fra alle faser, ikke bare denne',
  },
  {
    id: 'gamesWon',
    label: 'Antall vunne games',
    shortLabel: 'Vunne games',
    description: 'Totalt antall vunne games',
  },
  // Keep in step with DEFAULT_PADEL24_RANKING_ORDER in padel24.ts — this list
  // is the wizard's options (and the default written into a new tournament's
  // config), that one is the engine's fallback for tournaments with no stored
  // order. They describe the same chain and must not drift.
  {
    id: 'oppStrength',
    label: 'Motstandernes styrke',
    shortLabel: 'Motstand',
    description: 'Snitt-ELO på lagene du møtte i fasen — tøffest motstand først',
  },
];
export const DEFAULT_RANKING_PADEL24 = RANKING_OPTIONS_PADEL24.map((o) => ({
  id: o.id,
  enabled: true,
}));

// Ranking within a group for KM — FIP/national padel standard. Round-robin
// is ranked by: wins → head-to-head → sets-diff → games-diff → games won.
// (Reglamento FIP: partidos ganados, enfrentamiento directo, diferencia de
//  sets, diferencia de juegos, juegos a favor.)
export const RANKING_OPTIONS_KM = [
  {
    id: 'wins',
    label: 'Antall seire',
    shortLabel: 'Seire',
    description: 'Flest vunne kamper først',
  },
  {
    id: 'h2h',
    label: 'Innbyrdes oppgjør',
    shortLabel: 'Innbyrdes',
    description: 'Hvem vant det direkte oppgjøret (kun ved to like)',
  },
  {
    id: 'setsDiff',
    label: 'Sett-differanse',
    shortLabel: 'Sett-diff',
    description: 'Vunne sett minus tapte sett',
  },
  {
    id: 'gamesDiff',
    label: 'Games-differanse',
    shortLabel: 'Games-diff',
    description: 'Vunne games minus tapte games',
  },
  {
    id: 'gamesWon',
    label: 'Antall vunne games',
    shortLabel: 'Vunne games',
    description: 'Totalt antall vunne games',
  },
];
export const DEFAULT_RANKING_KM = RANKING_OPTIONS_KM.map((o) => ({ id: o.id, enabled: true }));

// Court prioritization for KM (club championship). Used to decide which
// match ends up on the "most attractive" court within the same match rotation.
// The rules are applied as tiebreakers — the first active rule ranks the matches,
// the second rule is used on a tie, and so on.
export const KM_COURT_PRIORITY_OPTIONS = [
  {
    id: 'class_a_first',
    label: 'A-klasse foran',
    shortLabel: 'A-klasse',
    description: 'A-klassens kamper får mest attraktiv bane før B/C/…',
  },
  {
    id: 'gender_priority',
    label: 'Herrer foran damer',
    shortLabel: 'Herrer',
    description: 'Ved likhet: herrekamper foran damekamper, mix sist',
  },
  {
    id: 'highest_seed',
    label: 'Høyest seed',
    shortLabel: 'Seed',
    description: 'Kampen der det beste laget (laveste seed-nummer) deltar',
  },
  {
    id: 'highest_team_elo_sum',
    label: 'Høyest samlet lag-ELO',
    shortLabel: 'Lag-ELO',
    description: 'Kampen med høyest samlet ELO på begge lag',
  },
  {
    id: 'highest_player_elo',
    label: 'Høyest enkeltspiller-ELO',
    shortLabel: 'Topp-spiller',
    description: 'Kampen med spilleren med høyest ELO',
  },
];
export const DEFAULT_KM_COURT_PRIORITY = KM_COURT_PRIORITY_OPTIONS.map((o) => ({
  id: o.id,
  enabled: true,
}));

// Pacing modes for KM: control how matches across classes are distributed over time.
//   'interleaved' (default): Alternating — round 1 of ALL classes first, then
//                            round 2, etc. Best when everyone should play evenly.
//   'prioritized':           Own order — finish one class at a time,
//                            in an order the admin chooses themselves.
// The engine also supports 'serial' (alphabetical, one-at-a-time) for backwards-compat with
// archived tournaments, but it has been removed as a choice in the UI.
export const KM_PACING_OPTIONS = [
  {
    id: 'interleaved',
    label: 'Vekselvis',
    description: 'Runde 1 av alle klasser først, så runde 2 av alle, osv. — alle spiller jevnt.',
  },
  {
    id: 'prioritized',
    label: 'Egen rekkefølge',
    description: 'Spill ferdig én klasse før neste begynner, i en rekkefølge du selv velger.',
  },
];

// Default ranking for Americano. Slightly different criteria — "points" instead of
// "games", since Americano counts points/games depending on the config.
export const RANKING_OPTIONS_AMERICANO = [
  { id: 'wins', label: 'Antall seire', shortLabel: 'Seire', description: 'Flest seire først' },
  { id: 'points', label: 'Poeng', shortLabel: 'Poeng', description: 'Sum av alle poeng vunnet' },
  {
    id: 'pointsDiff',
    label: 'Poengdifferanse',
    shortLabel: 'Poeng-diff',
    description: 'Vunne poeng minus tapte',
  },
  {
    id: 'h2h',
    label: 'Innbyrdes oppgjør',
    shortLabel: 'Innbyrdes',
    description: 'Hvem vant det direkte oppgjøret',
  },
];
export const DEFAULT_RANKING_AMERICANO = [
  { id: 'wins', enabled: true },
  { id: 'pointsDiff', enabled: true },
  { id: 'points', enabled: true },
  { id: 'h2h', enabled: true },
];

// The category colours carry white text (category chips, the calendar's match
// blocks, rank badges), so each is the AA shade of its original hue (#1144,
// owner decision 2026-11-02): it was #3b82f6 / #ec4899 / #8b5cf6, 3.68 / 3.53 /
// 4.23:1 under white. The shade is the lightest that keeps white at opacity
// 0.85 — the faded lines of a calendar block — at 4.5:1. Presentation only: no
// rule reads these. The KM components' own gender ternaries use the same
// values (apps/web/src/components/km).
export const KM_AVAILABLE_CATEGORIES = [
  { id: 'men', label: 'Herrer', emoji: '♂', gender: 'M', color: '#1961d3' },
  { id: 'women', label: 'Damer', emoji: '♀', gender: 'F', color: '#c01074' },
  { id: 'mix', label: 'Mix', emoji: '⚥', gender: 'mix', color: '#7845df' },
];

export const KM_CLASS_LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)); // A-Z

// Showcase scheduling: fixed time windows for the A-class semifinals/finals.
// Keys are the bucketKey for the A-class in Men's/Women's. Default OFF — does
// not affect the scheduler until the admin turns it on. Each window (semi/final) has
// its own day, time range, and courts — the needs differ. The times are pre-filled
// on the default Sunday (d_3); courts are chosen by the admin.
export const KM_DEFAULT_SHOWCASE = {
  men_A: {
    enabled: false,
    semi: { dayId: 'd_3', from: '11:00', to: '13:00', courts: [] },
    final: { dayId: 'd_3', from: '14:00', to: '16:00', courts: [] },
  },
  women_A: {
    enabled: false,
    semi: { dayId: 'd_3', from: '10:00', to: '12:00', courts: [] },
    final: { dayId: 'd_3', from: '13:00', to: '15:00', courts: [] },
  },
};

// Sentinel value for activeClassId when "All classes" is selected.
export const ALL_CLASSES = '__all__';

/**
 * A frozen copy of the CURRENT global settings.
 *
 * Frozen on purpose: the whole point of a snapshot is that it cannot change
 * under a calculation that is already running, and a plain object handed
 * around would quietly permit exactly that.
 */
export function snapshotEngineSettings(): Readonly<EngineSettingsValues> {
  return Object.freeze(EngineSettings.serialize());
}
